import React, { ChangeEvent, LegacyRef, useRef, useState } from "react";

import { CircularProgress, Grid } from "@mui/material";
import { Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { format, formatInTimeZone } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import { FiAlertOctagon, FiCheckCircle } from "react-icons/fi";
import { MdOutlineDownloadDone } from "react-icons/md";
import { VscInfo } from "react-icons/vsc";

import { useAppState } from "../../../context/StateContext";
import { ShipmentDocument } from "../../../models/shipment.document";
import { User } from "../../../models/user";
import UserService from "../../../services/user.service";
import { downloadAndGeneratePdf } from "../../../util/downloadAndGeneratePdf";
import { Status, DocumentAvailableAutoGenerate } from "../../../util/enums";
import { getUserFullname } from "../../../util/names";
import Button, { Color } from "../../Form/Button";
import { NotificationToast } from "../../Generic/Toast/NotificationToast";
import { DefaultTooltip } from "../../Generic/Tooltip";
import useStyles from "./styles";

interface Props {
  shipmentDocument: ShipmentDocument;
  onUploadDocument?: (url: string) => void;
  loadingUpload?: boolean;
  disabledUploadButton?: boolean;
  setButtonClicked?: () => void;
  onDownloadDocument?: (
    documentId: number,
    documentType?: DocumentAvailableAutoGenerate,
    onFinally?: () => void
  ) => void;
  onDownloadDocumentCsv?: (
    documentId: number,
    documentType?: DocumentAvailableAutoGenerate,
    onFinally?: () => void
  ) => void;
}

const MAX_SIZE = 2 * 1024 * 1024;

const DocumentsItem = (props: Props) => {
  const {
    onUploadDocument,
    loadingUpload,
    shipmentDocument,
    disabledUploadButton,
    setButtonClicked,
    onDownloadDocument,
    onDownloadDocumentCsv,
  } = props;
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { currentUser } = useAppState();

  const fileInput: LegacyRef<HTMLInputElement> = useRef(null);

  const [userList, setUserList] = useState<User[]>([]);
  const userUploaded = shipmentDocument?.userId
    ? userList.find((user) => Number(user.id) === Number(shipmentDocument.userId))
    : undefined;

  useQuery(
    ["users.list"],
    () =>
      UserService.getAll({
        include: JSON.stringify([
          {
            association: "usersCompanies",
            required: true,
            where: { status: Status.ACTIVE, companyId: currentUser?.companyId },
          },
        ]),
        sortBy: JSON.stringify(["name"]),
      }),
    {
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setUserList(response.data);
      },
    }
  );

  // TODO Felipe: need check how get that document can be generated by us
  const isAutoGenerated =
    shipmentDocument.name === DocumentAvailableAutoGenerate.COMMERCIAL_INVOICE_PACKING_LIST;

  const chooseFile = () => {
    if (fileInput && fileInput?.current) fileInput?.current?.click();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event?.target && event?.target?.files && event?.target?.files?.length > 0) {
      const file = event?.target?.files[0];

      const splittedFileName = file.name.split(".");
      const extensionFromName = splittedFileName[splittedFileName.length - 1];
      let validSize = true;

      if (file?.size && file?.size > MAX_SIZE) {
        NotificationToast.error(translate("theFileMustBeLessThan2mbInSize"));

        validSize = false;
      }

      if (file && extensionFromName === "pdf" && validSize) {
        const reader = new FileReader();

        reader.addEventListener("load", () => {
          const uri = reader.result?.toString();

          if (uri) {
            onUploadDocument?.(uri);
          }
        });
        reader.readAsDataURL(file);
      }
    }
    event.target.value = "";
  };

  const renderButtonUpload = () => {
    if (isAutoGenerated) {
      return (
        <>
          <Button customColor={Color.INFO} className={classes.button} disabled={true}>
            {translate("viewFile")}
          </Button>
          {onDownloadDocumentCsv ? (
            <Button customColor={Color.SECONDARY} className={classes.button} disabled={true}>
              {translate("downloadCsv")}
            </Button>
          ) : null}
        </>
      );
    }

    return (
      <>
        <DefaultTooltip
          title={`${translate("maxSize")} ${translate("2Mb")}, ${translate("formats")}: ${translate(
            "pdf"
          )}`}
        >
          <div>
            <Button
              data-cy="upload-button"
              className={classNames(classes.button, classes.uploadButton, {
                [classes.uploadingButton]: loadingUpload,
              })}
              customColor={Color.SECONDARY}
              onClick={() => {
                if (setButtonClicked) setButtonClicked();
                chooseFile();
              }}
              disabled={disabledUploadButton}
            >
              {loadingUpload && <CircularProgress size={16} className={classes.loading} />}
              {loadingUpload ? translate("uploading") : translate("upload")}
            </Button>
          </div>
        </DefaultTooltip>
        <input
          type="file"
          className={classes.input}
          ref={fileInput}
          value=""
          accept=".pdf"
          onChange={handleChange}
        />
      </>
    );
  };

  const renderTopInfo = () => {
    if (isAutoGenerated) {
      if (!onDownloadDocument) {
        return (
          <DefaultTooltip title={translate("bookingMustBeConfirmed")}>
            <div>
              <FiAlertOctagon size={20} className={classes.colorRed} />
            </div>
          </DefaultTooltip>
        );
      }

      return (
        <>
          <MdOutlineDownloadDone size={20} className={classes.colorGreen} />
          <span className={classes.generatedText}>{`${translate("documentGenerated")}`}</span>
        </>
      );
    }
    if (shipmentDocument?.uploaded && shipmentDocument?.uploadedAt) {
      return (
        <>
          <FiCheckCircle size={20} className={classes.colorBlue} />
          <span className={classes.uploadedText}>{`${translate("uploaded")}`}</span>
          <DefaultTooltip
            className={classes.uploadTooltip}
            title={`${translate("uploadMade")} ${translate("on").toLocaleLowerCase()} ${format(
              new Date(shipmentDocument?.uploadedAt),
              "dd/MM/yyyy HH:mm a"
            )} ${translate("by")} ${getUserFullname(userUploaded)}`}
          >
            <div>
              <VscInfo size={14} />
            </div>
          </DefaultTooltip>
        </>
      );
    }

    if (shipmentDocument?.required) {
      return (
        <DefaultTooltip title={translate("thisFileIsRequired")}>
          <div>
            <FiAlertOctagon size={20} className={classes.colorRed} />
          </div>
        </DefaultTooltip>
      );
    }

    return null;
  };

  const [loadingCsvFile, setLoadingCsvFile] = useState<boolean>(false);
  const [loadingViewFile, setLoadingViewFile] = useState<boolean>(false);
  const downloadDocumentAndViewFile = (urlToDownload: string) => {
    setLoadingViewFile(true);

    if (isAutoGenerated) {
      if (shipmentDocument.id && onDownloadDocument) {
        onDownloadDocument(
          shipmentDocument.id,
          DocumentAvailableAutoGenerate.COMMERCIAL_INVOICE_PACKING_LIST,
          () => {
            setLoadingViewFile(false);
          }
        );
      }

      return;
    }

    const onSuccess = () => {
      setLoadingViewFile(false);
    };

    const onFailure = () => {
      setLoadingViewFile(false);
    };

    downloadAndGeneratePdf(urlToDownload, onSuccess, onFailure);
  };

  const downloadCsvDocument = () => {
    if (isAutoGenerated && shipmentDocument.id && onDownloadDocumentCsv) {
      setLoadingCsvFile(true);
      onDownloadDocumentCsv(
        shipmentDocument.id,
        DocumentAvailableAutoGenerate.COMMERCIAL_INVOICE_PACKING_LIST,
        () => setLoadingCsvFile(false)
      );
    }
  };

  const renderBottomInfo = () => {
    if (
      (shipmentDocument?.uploaded && shipmentDocument?.uploadedAt && shipmentDocument?.file) ||
      (isAutoGenerated && onDownloadDocument)
    ) {
      return (
        <>
          <Button
            customColor={Color.INFO}
            className={classes.button}
            onClick={() => downloadDocumentAndViewFile(shipmentDocument.file || "")}
            disabled={loadingViewFile}
          >
            {loadingViewFile ? <CircularProgress size={16} /> : translate("viewFile")}
          </Button>
          {isAutoGenerated && onDownloadDocumentCsv ? (
            <Button
              customColor={Color.SECONDARY}
              className={classes.button}
              onClick={downloadCsvDocument}
              disabled={loadingCsvFile}
            >
              {loadingCsvFile ? <CircularProgress size={16} /> : translate("downloadCsv")}
            </Button>
          ) : null}
        </>
      );
    }

    return renderButtonUpload();
  };

  const renderDueDateInfo = () => {
    const hasDueDate = !!shipmentDocument?.dueDate;

    return (
      <Stack>
        <span className={classes.dueText}>{`${translate("dueDate")}`}</span>
        {hasDueDate ? (
          <span className={classes.bottomText}>{`${formatInTimeZone(
            shipmentDocument.dueDate,
            currentUser?.timezone?.nameFormatted ??
              Intl.DateTimeFormat().resolvedOptions().timeZone,
            "dd/MM/yyyy HH:mm a"
          )}`}</span>
        ) : (
          <span className={classes.notDefined}>{translate("notDefined")}</span>
        )}
      </Stack>
    );
  };

  return (
    <Grid item xs={12} sm={12} md={6} data-cy="document-item-component">
      <div className={classes.container}>
        <Stack direction={"row"} justifyContent={"space-between"} className={classes.topText}>
          <span>{shipmentDocument?.name}</span>
          <Stack
            direction={"row"}
            spacing={2}
            justifyContent={"center"}
            alignItems={"center"}
            className={classes.uploadText}
          >
            {renderTopInfo()}
          </Stack>
        </Stack>
        <Stack className={classes.bottom} direction={"row"} justifyContent={"space-between"}>
          {renderDueDateInfo()}
          {renderBottomInfo()}
        </Stack>
      </div>
    </Grid>
  );
};

export default DocumentsItem;
